import { notification as antdNotification } from "antd";
import classNames from "classnames";
import SVGIcon from "components/v2/Images/SVGIcon";
import Spin from "components/v2/Spin";
import Tooltip from "components/v2/Tooltip";
import { APP_VERSION } from "constants/v2/appConstants";
import { DownloadMatchContext } from "context/downloadMatch/context";
import { HighlightCreationContext } from "context/HighlightCreation/context";
import { SocialMediaPublishStatusContext } from "context/socialMediaPublishStatus/context";
import { TransformVideosContext } from "context/transformVideos/context";
import amplitude from "externals/amplitude";
import { useCallback, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import WebSocketDownloadMatch from "../WebSocket/WebSocketDownloadMatch";
import WebsocketTransformVideos from "../WebSocket/WebsocketTransformVideos";
import styles from "./style.module.css";

const AppNotifier = () => {
  const navigate = useNavigate();

  const { publishItems, setPublishItems, removeModal, retryPost } = useContext(SocialMediaPublishStatusContext);
  const { highlights, handleRemoveNotification } = useContext(HighlightCreationContext);
  const { downloads, stopDownloading, isDownloadListVisible, toggleDownloadList, handleWebhookStatusUpdate } =
    useContext(DownloadMatchContext);
  const {
    transformVideos,
    updateTransformVideo,
    streamIds,
    toggleTransformList,
    isTransformListVisible,
    handleCleanUpTransformVideos,
  } = useContext(TransformVideosContext);

  const handlePostToggleChange = useCallback(
    (groupKey) => {
      try {
        setPublishItems((prev) =>
          prev.map((group) =>
            group.key === groupKey
              ? {
                  ...group,
                  isToggleOpen: !group?.isToggleOpen,
                }
              : group,
          ),
        );
      } catch (error) {
        console.error(error);
      }
    },
    [setPublishItems],
  );

  const handleHLViewClick = useCallback(
    (highlight) => {
      try {
        navigate(`/clips/${highlight.streamId}`, {
          state: { fromHighlightNotification: true, clipListId: highlight?.clipListId },
        });
        handleRemoveNotification(highlight.clipListId);
      } catch (error) {
        console.error(error);
      }
    },
    [handleRemoveNotification, navigate],
  );

  // NB: useEffect to trigger antd-notifications for social media publish items.
  useEffect(() => {
    if (!publishItems?.length) return;

    publishItems?.forEach((group) => {
      // Succesful posts count for the group.
      const successfulCount = group?.posts?.filter(
        (post) => post?.status === "success" || post?.status === "completed",
      ).length;

      // Failed posts count for the group.
      const failedCount = group?.posts?.filter((post) => post?.status === "failed" || post?.status === "error").length;

      const title = (
        <div className={styles.titleContainer}>
          <div className={styles.titleText} data-testid="AppNotifier-SocialMedia-Title">
            {group?.posts?.length > 0 && failedCount > 0 && failedCount === group?.posts?.length
              ? "Publish Failed!"
              : group?.posts?.length === successfulCount + failedCount
                ? "Publish Completed!"
                : "Publish Initiated Successfully!"}
          </div>
          <div onClick={() => handlePostToggleChange(group?.key)}>
            <SVGIcon
              src={`components/v2/caret.svg`}
              className={classNames(styles.expandIcon, {
                [styles.revertedExpandIcon]: !group?.isToggleOpen,
              })}
            />
          </div>
        </div>
      );

      const description = group?.isToggleOpen ? (
        <>
          <div className={styles.countDetailsContainer}>
            {successfulCount} Successful - {failedCount} Failed
          </div>
          {group?.posts?.map((post) => (
            <div key={post.id} className={styles.listContainer}>
              <div className={styles.profileText}>
                {post.platform}
                <span className={styles.profileNameText}>&nbsp;-&nbsp;{post.profileName}</span>
              </div>
              <div>
                {post.status === "loading" ? (
                  <Spin className={styles.loader} size="small" />
                ) : post?.postUrl ? (
                  <div className={styles.iconWrapper}>
                    <SVGIcon
                      src="components/v2/Modal/externalLink.svg"
                      className={styles.linkIcon}
                      onClick={() => {
                        window.open(post?.postUrl, "_blank");
                      }}
                    />
                    <SVGIcon src="components/v2/Modal/circleCheck.svg" className={styles.icon} />
                  </div>
                ) : post.status !== "success" ? (
                  <div className={styles.iconWrapper}>
                    {post?.isRetryAllowed ? (
                      <div className={styles.retryBtn} onClick={() => retryPost(group?.key, post?.id)}>
                        RETRY
                      </div>
                    ) : null}
                    <Tooltip placement="left" title={post?.error} arrow>
                      <SVGIcon src="components/v2/Modal/error.svg" className={styles.icon} />
                    </Tooltip>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </>
      ) : null;

      // antd-notification configuration for social media publish items.
      antdNotification.open({
        key: group.key,
        message: title,
        description,
        duration: 120,
        onClose: () => removeModal(group.key),
        className: styles.notificationContainer,
      });
    });
  }, [handlePostToggleChange, publishItems, removeModal, retryPost]);

  // NB: useEffect to trigger antd-notifications for drag and drop HL generation items.
  useEffect(() => {
    if (!highlights?.length) return;

    highlights
      ?.filter((item) => !item.isHidden)
      ?.forEach((highlight) => {
        const isGeneratingHL = highlight?.status === "PROCESSING" || highlight?.status === "GENERATING";
        const isHLGenerationSuccess = highlight?.status === "SUCCESS";
        const isHLGenerationCancelled = highlight?.status === "CANCELLED";

        if (isHLGenerationCancelled) {
          return;
        }

        if (isHLGenerationSuccess) {
          amplitude.logEvent("highlight_generation_completed", {
            clipListId: highlight?.clipListId,
            stream_id: highlight?.streamId,
            aspect_ratio: highlight?.aspectRatio,
            version: APP_VERSION.V2,
          });
        }

        const title = (
          <div className={styles.titleContainer}>
            {isGeneratingHL ? (
              <div className={styles.highlightContentWrapper}>
                <Spin size="small" className={styles.spin} />
                <div className={styles.titleText}>Highlight generation initiated</div>
              </div>
            ) : isHLGenerationSuccess ? (
              <div className={styles.highlightContentWrapper}>
                <SVGIcon src="components/v2/Modal/circleCheck.svg" className={styles.icon} />
                <div className={styles.titleText}>Highlight generated successfully</div>
              </div>
            ) : (
              <div className={styles.highlightContentWrapper}>
                <SVGIcon src="components/v2/Modal/error.svg" className={styles.icon} />
                <div className={styles.titleText}>Highlight generation failed</div>
              </div>
            )}
            {isHLGenerationSuccess ? (
              <div className={styles.retryBtn} onClick={() => handleHLViewClick(highlight)}>
                VIEW
              </div>
            ) : null}
          </div>
        );

        antdNotification.open({
          key: highlight.clipListId,
          message: title,
          description: null,
          duration: 60,
          onClose: () => handleRemoveNotification(highlight.clipListId),
          className: styles.notificationContainer,
        });
      });
  }, [highlights, handleRemoveNotification, navigate, handleHLViewClick]);

  // NB: useEffect to trigger antd-notifications for download match items.
  useEffect(() => {
    if (!Object.keys(downloads).length > 0) return;

    const downloadItems = Object.values(downloads);

    const title = (
      <div className={styles.titleContainer}>
        <SVGIcon
          src={`components/v2/caret.svg`}
          className={classNames(styles.expandIcon, styles.downloadExpandIcon, {
            [styles.revertedExpandIcon]: !isDownloadListVisible,
          })}
          onClick={toggleDownloadList}
        />
        <div className={styles.downloadTitle} data-testid="AppNotifier-ExportMatch-Title">
          Exporting {downloadItems.length} video{downloadItems.length > 1 ? "s" : ""}
        </div>
      </div>
    );

    const successCount = downloadItems.filter((d) => d.status === "SUCCESSFUL" || d.status === "COMPLETED").length;
    const failedCount = downloadItems.filter((d) => d.status === "FAILED").length;
    const progressCount = downloadItems.filter(
      (d) => d.status === "INITITATED" || d.status === "PROCESSING" || d.status === "IN_PROGRESS",
    ).length;

    const description = (
      <>
        <div className={classNames(styles.countDetailsContainer, styles.downloadCountDetailsContainer)}>
          <span>
            {successCount} Successful · {failedCount} Failed
          </span>
          {progressCount > 0 ? (
            <Spin className={styles.loader} size="small" />
          ) : successCount === downloadItems.length ? (
            <SVGIcon src="components/v2/Modal/circleCheck.svg" className={styles.icon} />
          ) : (
            <SVGIcon src="components/v2/Modal/error.svg" className={styles.icon} />
          )}
        </div>
        <div className={classNames(styles.downloadList, { [styles.downloadListVisible]: isDownloadListVisible })}>
          {downloadItems.map((download) => {
            const isInProgress = download.status === "IN_PROGRESS";
            const isSuccessful = (download.status === "SUCCESSFUL" || download.status === "COMPLETED") && !isInProgress;
            const isInitiated = download.status === "INITIATED";
            const isProcessing = download.status === "PROCESSING";
            const isFailed = download.status === "FAILED";

            return (
              <div key={download?.id} className={styles.listContainer}>
                <div className={styles.downloadItemTitle}>
                  {download.title}{" "}
                  <span className={styles.downloadItemSpan}>
                    · MP4 · {isInitiated ? "Queued" : null}
                    {isProcessing ? `Exporting ${download.exportProgress || 0}%` : null}
                    {isInProgress ? `Downloading ${download.progress || 0}%` : null}
                    {isSuccessful ? "Downloaded" : null}
                    {isFailed ? "Failed" : null}
                  </span>
                </div>
                <div>
                  {isInProgress || isInitiated || isProcessing ? (
                    <Spin className={styles.loader} size="small" />
                  ) : isSuccessful ? (
                    <div className={styles.iconWrapper}>
                      {download?.streamId && (
                        <SVGIcon
                          src="components/v2/Modal/externalLink.svg"
                          className={styles.linkIcon}
                          onClick={() => {
                            const streamId = download?.streamId;
                            const url = `/clips/${streamId}?filters=%7B"currentTab"%3A"matchVideo"%7D`;

                            window.open(url, "_blank");
                          }}
                        />
                      )}
                      <SVGIcon src="components/v2/Modal/circleCheck.svg" className={styles.icon} />
                    </div>
                  ) : (
                    <SVGIcon src="components/v2/Modal/error.svg" className={styles.icon} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );

    antdNotification.open({
      key: "downloads",
      message: title,
      description,
      duration: 0,
      placement: "bottomRight",
      onClose: () => stopDownloading(),
      className: classNames(styles.notificationContainer, styles.downloadContainer),
    });
  }, [downloads, isDownloadListVisible, stopDownloading, toggleDownloadList]);

  // NB: useEffect to trigger antd-notifications for transform videos.
  useEffect(() => {
    if (!Object.keys(transformVideos).length > 0) return;

    const transformVideoItems = Object.values(transformVideos);

    const title = (
      <div className={styles.titleContainer}>
        <SVGIcon
          src={`components/v2/caret.svg`}
          className={classNames(styles.expandIcon, styles.downloadExpandIcon, {
            [styles.revertedExpandIcon]: !isTransformListVisible,
          })}
          onClick={toggleTransformList}
        />
        <div className={styles.downloadTitle} data-testid="AppNotifier-TransformVideos-Title">
          Exporting {transformVideoItems.length} video{transformVideoItems.length > 1 ? "s" : ""}
        </div>
      </div>
    );

    const successCount = transformVideoItems.filter((d) => d.status === "COMPLETED").length;
    const failedCount = transformVideoItems.filter((d) => d.status === "FAILED").length;
    const progressCount = transformVideoItems.filter(
      (d) => d.status === "INITIATED" || d.status === "PROCESSING",
    ).length;

    const description = (
      <>
        <div className={classNames(styles.countDetailsContainer, styles.downloadCountDetailsContainer)}>
          <span>
            {successCount} Successful · {failedCount} Failed
          </span>
          {progressCount > 0 ? (
            <Spin className={styles.loader} size="small" />
          ) : successCount === transformVideoItems.length ? (
            <SVGIcon src="components/v2/Modal/circleCheck.svg" className={styles.icon} />
          ) : (
            <SVGIcon src="components/v2/Modal/error.svg" className={styles.icon} />
          )}
        </div>
        <div className={classNames(styles.downloadList, { [styles.downloadListVisible]: isTransformListVisible })}>
          {transformVideoItems.map((transformVideo) => {
            const isSuccessful = transformVideo.status === "COMPLETED";
            const isInitiated = transformVideo.status === "INITIATED";
            const isProcessing = transformVideo.status === "PROCESSING";
            const isFailed = transformVideo.status === "FAILED";

            return (
              <div key={transformVideo?.requestId} className={styles.listContainer}>
                <div className={styles.downloadItemTitle}>
                  {transformVideo.title}{" "}
                  <span className={styles.downloadItemSpan}>
                    · {transformVideo.format} · {isInitiated ? "Queued" : null}
                    {isProcessing ? `Exporting ${transformVideo.progress || 0}%` : null}
                    {isSuccessful ? "Exported" : null}
                    {isFailed ? "Failed" : null}
                  </span>
                </div>
                <div>
                  {isInitiated || isProcessing ? (
                    <Spin className={styles.loader} size="small" />
                  ) : isSuccessful ? (
                    <SVGIcon src="components/v2/Modal/circleCheck.svg" className={styles.icon} />
                  ) : (
                    <SVGIcon src="components/v2/Modal/error.svg" className={styles.icon} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );

    antdNotification.open({
      key: "transformVideos",
      message: title,
      description,
      duration: 0,
      placement: "bottomRight",
      onClose: () => handleCleanUpTransformVideos(),
      className: classNames(styles.notificationContainer, styles.downloadContainer),
    });
  }, [transformVideos, toggleTransformList, isTransformListVisible, handleCleanUpTransformVideos]);

  return (
    <>
      <WebSocketDownloadMatch downloads={downloads} handleWebhookStatusUpdate={handleWebhookStatusUpdate} />
      <WebsocketTransformVideos
        transformVideos={transformVideos}
        updateTransformVideo={updateTransformVideo}
        streamIds={streamIds}
      />
    </>
  );
};

export default AppNotifier;
